/**
 * Le précall du masteur, en deux temps, comme l'ancien `PreCallScreen.js` quand c'est un tuteur qui y passe :
 *
 * `useTutorPreCallEntry` — l'écran, avant tout test : la séance de la route (le compte relu une fois, 6 s au
 * plus), le choix du navigateur hors de Chrome sur ordinateur, la barre, l'heure, les élèves déjà là, et
 * l'entrée elle-même (`openLive`). Il ne monte pas le moteur : l'ancien ne montait son test qu'une fois la
 * séance connue et le navigateur choisi (`PreCallScreen.js:468`, `:492`) — derrière le choix, pas de
 * `precall.opened`, pas de mesure de la connexion, pas de webcam allumée.
 *
 * `useTutorPreCallCheck` — le test du matériel, monté ensuite (`TutorPreCallScreen`, sous sa frontière
 * d'erreur) : le moteur de l'élève avec les mots du tuteur (`audience: "tutor"`), lancé par « Vérifier mon
 * matériel » (il ne part pas seul), et le geste d'entrer.
 *
 * Entrer : `precall.join` (séance trouvée seulement), puis `apiIdle(createConversation)`,
 * `setIsLiveModeEnabled(true, id)` et la relecture du compte (`PreCallScreen.js:197-202`, `:320-329`).
 * Jamais de signalement au technicien, jamais le starting block : c'était le filet de l'élève
 * (`PreCallScreen.js:297`).
 *
 * Nouveau, au natif : les appareils sont rendus avant d'entrer. Un objectif et un micro ne servent bien
 * qu'un client ; la salle du tuteur demande les permissions puis publie les siens
 * (`engine/live/tutor/tutorMediaAccess`). « Entrer » démonte donc l'image de la caméra et fait fermer
 * l'enregistreur du micro par le moteur, puis entre au bout de `CAMERA_HANDOFF_MS` — la remise partagée avec
 * l'élève (`useLiveEntryDeviceCheck`). `precall.join` part alors, avec le bilan du test lu au geste. Le web
 * ne change pas.
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest } from "../../../../../../data/redux/actions/api"
import { setIsLiveModeEnabled } from "../../../../../../data/redux/actions/liveMode"
import { setMuteVideo } from "../../../../../../data/redux/actions/videoCall"
import { authService } from "../../../../../../data/services/authService"
import { liveService } from "../../../../../../data/services/liveService"
import { type DeviceCheckReport } from "../../../../../../engine/precall/deviceCheckMachine"
import { reportVerdicts } from "../../../../../../engine/precall/liveEntry"
import { PRECALL_EVENT, trackPrecall } from "../../../../../../engine/precall/precallTracking"
import { type ApiEntryAccount } from "../../../../adapters/student/LiveEntry/liveEntryAdapter"
import { toTutorPreCall } from "../../../../adapters/tutor/PreCall/tutorPreCallAdapter"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"
import { entryTestShown } from "../../../../view-models/student/LiveEntry/deviceCheckViewModel"
import { liveEntryEquipment, liveEntryJoin } from "../../../../view-models/student/LiveEntry/liveEntryLobbyViewModel"
import {
    tutorPreCallHeading,
    tutorPreCallStatus,
    tutorPresentLabel,
} from "../../../../view-models/tutor/PreCall/tutorPreCallViewModel"
import {
    useLiveEntryBrowser,
    useLiveEntryClock,
    useLiveEntryDeviceCheck,
    useLiveEntrySessionWait,
    useLiveEntryTracking,
    useLiveEntryVideoMuted,
} from "../../student/LiveEntry/useLiveEntryParts"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReduxState = any
type Navigation = { goBack: () => void }

/** Ce que `precall.join` lit du test : son bilan au geste d'entrer. */
export type TutorJoinReport = Pick<DeviceCheckReport, "allDone" | "microphone" | "speaker" | "camera" | "network">

/**
 * Le test est tombé (la frontière d'erreur) : on le tient pour fini, comme l'ancien (`PreCallCheckBoundary`,
 * `onFailed` : `allDone`, aucun verdict) — l'entrée reprend sa forme de bouton, sans rien affirmer du matériel.
 */
export const FAILED_CHECK_REPORT = { allDone: true } as TutorJoinReport

export function useTutorPreCallEntry(liveSessionId: string) {
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as Navigation

    const account = useSelector((state: ReduxState) => selectAccount(state)) as ApiEntryAccount | null
    const liveParticipants = useSelector((state: ReduxState) => state?.liveMode?.liveParticipants) as
        (number | string)[] | undefined
    // La vidéo coupée, lue ici une fois : la bascule avant le test, puis le test (`useTutorPreCallCheck`).
    const isVideoMuted = useLiveEntryVideoMuted()

    const model = useMemo(
        () => toTutorPreCall({ account, liveSessionId, liveParticipants }),
        [account, liveSessionId, liveParticipants]
    )
    const sessionTimedOut = useLiveEntrySessionWait(model.found, account?.id)
    const tracking = useLiveEntryTracking(liveSessionId)
    const { browser, dismissBrowser } = useLiveEntryBrowser()
    const testShown = entryTestShown(model.found, sessionTimedOut)
    const now = useLiveEntryClock()

    const page = useMemo(
        () => ({
            heading: tutorPreCallHeading(model),
            status: tutorPreCallStatus(model, now),
            present: model.present,
            presentLabel: tutorPresentLabel(model.present),
        }),
        [model, now]
    )

    /** Ouvre le live, une fois ; `report` : le bilan du test au geste, pour `precall.join`. */
    const enteredRef = useRef(false)
    const openLive = useCallback(
        (report: TutorJoinReport) => {
            if (enteredRef.current) return
            enteredRef.current = true
            // Les propriétés de l'ancien écran, rien de plus : ni l'issue ni les permissions de l'élève.
            if (model.found) trackPrecall(PRECALL_EVENT.JOIN, { ...tracking, ...reportVerdicts(report) })
            dispatch(apiIdle(liveService.createConversation) as never)
            dispatch(setIsLiveModeEnabled(true, liveSessionId))
            dispatch(apiRequest(authService.getUser) as never)
        },
        [dispatch, liveSessionId, model.found, tracking]
    )

    return {
        model,
        page,
        tracking,
        browser,
        dismissBrowser,
        /** Le test se monte : la séance connue (ou 6 s passées), le navigateur choisi. */
        checkMounted: testShown && browser == null,
        /** La caméra avant le test : la même bascule que l'image du test (`setMuteVideo`). */
        camera: { on: !isVideoMuted, toggle: () => dispatch(setMuteVideo(!isVideoMuted)) },
        isVideoMuted,
        openLive,
        onBack: () => navigation.goBack(),
    }
}

export type TutorPreCallEntry = ReturnType<typeof useTutorPreCallEntry>

export function useTutorPreCallCheck(entry: Pick<TutorPreCallEntry, "tracking" | "openLive" | "isVideoMuted">) {
    const { openLive } = entry
    // Le bilan du test au premier geste d'entrer : `precall.join` le porte quand le live s'ouvre.
    const reportRef = useRef<TutorJoinReport | null>(null)
    const { check, vm, releasing, enter } = useLiveEntryDeviceCheck({
        audience: "tutor",
        tracking: entry.tracking,
        onEntered: () => openLive(reportRef.current ?? FAILED_CHECK_REPORT),
        releasesMicrophone: true,
        isVideoMuted: entry.isVideoMuted,
    })
    const lobby = useMemo(() => ({ equipment: liveEntryEquipment(vm), join: liveEntryJoin(vm) }), [vm])

    const { report } = check.view
    const feedShown = lobby.equipment.feedMounted
    const onEnter = useCallback(() => {
        reportRef.current ??= report
        enter(feedShown)
    }, [enter, feedShown, report])

    return { check, vm, lobby, releasing, onEnter }
}
